
import React from 'react';
import { useAdminData, AdminSubmission } from '@/hooks/useAdminData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, 
  Building2, 
  Mail, 
  Users, 
  Euro, 
  Zap, 
  Clock, 
  Target,
  Lightbulb,
  TrendingUp
} from 'lucide-react';
import {
  QUICKSCAN_PRIORITY_SCALE,
  orderQuickscanPriorities,
  quickscanPriorityPercentage,
} from '@/lib/quickscan-priorities';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface AdminSubmissionDetailProps {
  submissionId: string;
  onBack: () => void;
}

const AdminSubmissionDetail = ({ submissionId, onBack }: AdminSubmissionDetailProps) => {
  const { submissions } = useAdminData();
  const submission = submissions.find(s => s.id === submissionId);

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission niet gevonden</h2>
          <Button onClick={onBack}>Terug naar overzicht</Button>
        </div>
      </div>
    );
  }

  const formatExpansionDetails = (planType: string, details: any) => {
    switch (planType) {
      case 'location':
        return (
          <div className="space-y-2">
            <p><strong>Locatie:</strong> {details.location}</p>
            <p><strong>Eigendom:</strong> {details.ownership === 'buy' ? 'Kopen' : 'Huren'}</p>
            <p><strong>Oppervlakte:</strong> {details.surfaceArea} m²</p>
            <p><strong>Doel:</strong> {details.purpose}</p>
          </div>
        );
      case 'sustainability':
        return (
          <div className="space-y-2">
            <p><strong>Certificeringen:</strong> {details.certifications?.join(', ') || 'Geen'}</p>
            <p><strong>Doelstellingen:</strong> {details.targets?.join(', ') || 'Geen'}</p>
            <p><strong>Tijdlijn:</strong> {details.timeline} jaar</p>
            <p><strong>CO2 reductie doel:</strong> {details.co2ReductionTarget}%</p>
            <p><strong>Budget:</strong> {details.budget}</p>
          </div>
        );
      case 'growth':
        return (
          <div className="space-y-2">
            <p><strong>Groei percentage:</strong> {details.growthPercentage}%</p>
            <p><strong>Tijdsperiode:</strong> {details.timeframe}</p>
            <p><strong>Groei types:</strong> {details.growthTypes?.join(', ') || 'Geen'}</p>
            <p><strong>Extra werknemers:</strong> {details.additionalEmployees}</p>
          </div>
        );
      case 'equipment':
        return (
          <div className="space-y-2">
            <p><strong>Equipment types:</strong> {details.equipmentTypes?.join(', ') || 'Geen'}</p>
            <p><strong>Energieverbruik:</strong> {details.energyConsumption} kWh</p>
            <p><strong>Vermogen:</strong> {details.powerConsumption} kW</p>
            <p><strong>Aantal:</strong> {details.quantity}</p>
            <p><strong>Bedrijfsuren:</strong> {details.operatingHours} uur/dag</p>
            <p><strong>Implementatie:</strong> {details.timeline}</p>
          </div>
        );
      default:
        return <pre className="text-sm">{JSON.stringify(details, null, 2)}</pre>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button variant="ghost" onClick={onBack} className="mr-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Terug
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {submission.company_name || 'Quickscan Details'}
              </h1>
              <p className="text-gray-600">
                Submission van {format(new Date(submission.created_at), 'dd MMMM yyyy HH:mm', { locale: nl })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Basis informatie */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Bedrijfsinformatie
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bedrijfsnaam</label>
                    <p className="text-lg">{submission.company_name || 'Niet opgegeven'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-lg flex items-center gap-2">
                      {submission.email ? (
                        <>
                          <Mail className="h-4 w-4" />
                          {submission.email}
                        </>
                      ) : (
                        'Niet opgegeven'
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Bedrijfstype</label>
                    <p className="text-lg">
                      <Badge variant="outline">{submission.business_type}</Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Aantal werknemers</label>
                    <p className="text-lg flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {submission.employee_count}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Energie informatie */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Energie informatie
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Maandelijkse energiekosten</label>
                    <p className="text-lg flex items-center gap-2">
                      <Euro className="h-4 w-4" />
                      €{submission.monthly_energy_costs}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Piekuren energie</label>
                    <p className="text-lg">
                      {submission.energy_peak_hours.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {submission.energy_peak_hours.map(hour => (
                            <Badge key={hour} variant="secondary">{hour}:00</Badge>
                          ))}
                        </div>
                      ) : (
                        'Geen piekuren opgegeven'
                      )}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-500">Huidige oplossingen</label>
                  <div className="mt-2">
                    {submission.current_solutions.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {submission.current_solutions.map(solution => (
                          <Badge key={solution} variant="outline">{solution}</Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">Geen huidige oplossingen</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Uitbreidingsplannen */}
            {submission.expansion_plans.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Uitbreidingsplannen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {submission.expansion_plans.map(plan => (
                      <Badge key={plan} variant="outline">{plan}</Badge>
                    ))}
                  </div>
                  
                  {Object.entries(submission.expansion_details).map(([planType, details]) => (
                    <div key={planType}>
                      <h4 className="font-semibold mb-2 capitalize">{planType} Details:</h4>
                      {formatExpansionDetails(planType, details)}
                      <Separator className="mt-4" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Prioriteiten */}
            {Object.keys(submission.priorities).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Prioriteiten
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* De schuifjes lopen 1 t/m 10; met /5 werd een 10 een balk van 200%. */}
                    {orderQuickscanPriorities(submission.priorities).map(({ id, label, value }) => (
                      <div key={id} className="flex justify-between items-center">
                        <span>{label}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${quickscanPriorityPercentage(value)}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-10">{value}/{QUICKSCAN_PRIORITY_SCALE.max}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar met resultaten */}
          <div className="space-y-6">
            {/* Resultaten */}
            {submission.energy_potential_score && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Resultaten
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary mb-1">
                      {submission.energy_potential_score}%
                    </div>
                    <p className="text-sm text-gray-500">Energy Potential Score</p>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3">
                    {submission.yearly_annual_savings && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Jaarlijkse besparing</span>
                        <span className="font-semibold">€{submission.yearly_annual_savings}</span>
                      </div>
                    )}
                    
                    {submission.co2_reduction && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">CO2 reductie</span>
                        <span className="font-semibold">{submission.co2_reduction} ton</span>
                      </div>
                    )}
                    
                    {submission.roi_months && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm">ROI periode</span>
                        <span className="font-semibold flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {submission.roi_months} maanden
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Aanbevelingen */}
            {submission.recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    Aanbevelingen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {submission.recommendations.map((rec, index) => (
                      <li key={index} className="text-sm p-2 bg-gray-50 rounded">
                        {rec}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle>Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Submission ID</span>
                  <span className="font-mono text-xs">{submission.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Aangemaakt</span>
                  <span>{format(new Date(submission.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubmissionDetail;
